import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, ArrowLeft, Printer, DownloadSimple, ShieldCheck, LinkedinLogo, Share, Certificate } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";

/**
 * Phase 3 — Security-Protected Verification Screen
 *  - Token users: green "live" verification screen with HMAC code rotating every 30s
 *    + ticking clock + countdown to next rotation.
 *  - Credentialed users: branded named certificate card.
 *  - Both: PDF download via /api/certificate/:id/pdf
 */
export default function CertificatePage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  // Staff who took the course "as a grade" keep that context when heading back.
  const viewGrade = searchParams.get("grade");
  const backToCourse = `/course/${id}${viewGrade ? `?grade=${viewGrade}` : ""}`;
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [err, setErr] = useState("");
  const [now, setNow] = useState(new Date());
  const [verify, setVerify] = useState(null); // { code, server_time_unix, next_rotation_in }
  const [serverOffset, setServerOffset] = useState(0); // ms between server and client
  const [shareAllowed, setShareAllowed] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const nav = useNavigate();

  // Sharing policy: public track ON; school system OFF unless the board enabled it.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/certificate/share-policy");
        setShareAllowed(!!data.share_allowed);
      } catch (_) { setShareAllowed(false); }
    })();
  }, []);

  // Load course
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/courses/${id}`);
        setCourse(data);
      } catch (e) { setErr(formatApiError(e)); }
    })();
  }, [id]);

  // Local clock tick
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // For token users on a completed course: fetch + rotate verification code
  useEffect(() => {
    if (!course || !user) return;
    if (course.progress_percent !== 100) return;
    if (user.kind !== "token") return;
    let cancelled = false;
    let timer;
    const fetchCode = async () => {
      try {
        const { data } = await api.get(`/certificate/${id}/verify`);
        if (cancelled) return;
        setVerify(data);
        setServerOffset(data.server_time_unix * 1000 - Date.now());
        // Schedule next fetch right after rotation
        timer = setTimeout(fetchCode, (data.next_rotation_in + 1) * 1000);
      } catch (e) {
        if (!cancelled) setErr(formatApiError(e));
      }
    };
    fetchCode();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [course, user, id]);

  if (!user) return null;
  if (err) return <div className="p-8 text-[#FF5100]">{err}</div>;
  if (!course) return <div className="p-8 text-[#000000]">Loading...</div>;

  const passedCourse = course.progress_percent === 100;
  const isToken = user.kind === "token";
  const displayName = isToken
    ? (`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.name || `Token: ${user.code}`)
    : user.name;

  if (!passedCourse) {
    return (
      <div className="bg-grain min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <h2 className="text-2xl font-bold font-display mb-3">Not yet certified</h2>
        <p className="text-[#000000] mb-5">Finish all lessons and quizzes to view your certificate.</p>
        <button data-testid="back-to-course-button" className="btn-primary" onClick={() => nav(backToCourse)}>Back to course</button>
      </div>
    );
  }

  // Compute server-synced time for the live clock display
  const liveTime = new Date(Date.now() + serverOffset);
  const secondsUntilRotation = verify
    ? Math.max(0, 30 - (Math.floor(liveTime.getTime() / 1000) % 30))
    : null;

  const certId = `E650-${(user.id || "").slice(0, 8).toUpperCase()}-${(id || "").slice(0, 6).toUpperCase()}`;
  const verifyUrl = `${window.location.origin}/verify?course_id=${id}&principal_id=${user.id}&cert_id=${certId}`;

  const shareLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verifyUrl)}`, "_blank");
  };

  const shareGeneric = async () => {
    const shareData = { title: course.title, text: `I completed ${course.title}!`, url: verifyUrl };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(verifyUrl);
      }
    } catch (_) { /* user cancelled or unsupported */ }
  };

  const downloadShareImage = async () => {
    setShareBusy(true);
    try {
      const { data } = await api.get(`/certificate/${id}/share-image`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "image/png" }));
      const a = document.createElement("a");
      a.href = url; a.download = "my-achievement.png"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = "Couldn't create the share image.";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) {}
      setErr(msg);
    } finally { setShareBusy(false); }
  };

  return (
    <div className={isToken ? "min-h-screen" : "bg-grain min-h-screen"}
         style={isToken ? { background: "linear-gradient(160deg, #001f0c, #003814)" } : {}}>
      <div className="max-w-3xl mx-auto px-5 py-10">
        <button data-testid="cert-back-button" className="flex items-center gap-1 mb-4 rounded-full px-4 py-2 text-sm font-bold cursor-pointer"
          style={{ background: "#FFFFFF", color: "#000000", border: "1px solid #C4C8CE" }}
          onClick={() => nav(backToCourse)}>
          <ArrowLeft size={16} weight="bold" /> Back
        </button>

        <motion.div
          initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 220, damping: 22 }}
          className="rounded-3xl p-8 sm:p-12 border-2 shadow-2xl"
          style={{
            background: isToken
              ? "linear-gradient(135deg, #00AA45, #006A2C)"
              : "linear-gradient(135deg, #FFFFFF, #1A1F2E)",
            borderColor: isToken ? "#7CFFA0" : "#CCFF00",
          }}
          data-testid="certificate-card"
        >
          <CheckCircle size={64} weight="fill" color={isToken ? "#FFFFFF" : "#CCFF00"} />
          <div className="text-xs uppercase tracking-[0.3em] mt-4 opacity-80">{isToken ? "Live Proof of Completion" : "Certificate of Completion"}</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold font-display mt-2 leading-tight">
            {course.title}
          </h1>
          <div className="mt-6 text-sm uppercase tracking-[0.2em] opacity-80">Awarded to</div>
          <div className="text-3xl font-extrabold font-display mt-1" data-testid="certificate-name">
            {displayName}
          </div>

          {isToken ? (
            <div className="mt-8 p-5 rounded-2xl bg-[#001a08]/85 border border-white/15 text-white">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={20} weight="fill" color="#CCFF00" />
                <div className="text-xs uppercase tracking-[0.25em] text-white">Live verification (anti-screenshot)</div>
              </div>
              <div className="grid grid-cols-2 gap-4 items-start">
                <div>
                  <div className="text-xs text-[#A7D9B2]">Server time</div>
                  <div className="text-3xl font-extrabold font-display tabular-nums text-white" data-testid="live-clock">
                    {liveTime.toLocaleTimeString()}
                  </div>
                  <div className="text-xs text-[#A7D9B2] mt-0.5" data-testid="live-date">
                    {liveTime.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[#A7D9B2]">Verification code</div>
                  <div className="text-3xl font-extrabold font-mono tracking-[0.3em] text-[#CCFF00]" data-testid="verification-code"
                    style={{ WebkitTextStroke: "1px #000", textShadow: "0 1px 2px rgba(0,0,0,0.55)" }}>
                    {verify?.code || "------"}
                  </div>
                  {verify && (
                    <div className="text-xs text-[#A7D9B2] mt-0.5" data-testid="verification-countdown">
                      Rotates in {secondsUntilRotation}s
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-[#D9F2E0] mt-4">
                Teachers: confirm the clock is ticking and the verification code matches a live device.
                Codes rotate every 30s and cannot be screenshotted.
              </p>
              <p className="text-xs text-[#A7D9B2] mt-2" data-testid="cert-pdf-hint">
                Looking for the printable gold-seal certificate or wallet card? Use "Printable certificates" below —
                your teacher can also print them from the class roster.
              </p>
            </div>
          ) : (
            <div className="text-[#000000] mt-6 text-sm">
              Issued on {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}.
              The Self-Agency Project official certification.
            </div>
          )}

          <div className="flex gap-3 mt-8 flex-wrap items-center">
            <button data-testid="cert-download-button" className="btn-primary flex items-center gap-2" onClick={() => nav(`/certificate/${id}`)}>
              <Certificate size={18} weight="fill" /> Printable certificates
            </button>
            <button data-testid="cert-print-button" className="btn-secondary flex items-center gap-2" onClick={() => window.print()}>
              <Printer size={18} weight="bold" /> Print
            </button>
            {shareAllowed && (
              <button data-testid="cert-share-image-button" className="btn-secondary flex items-center gap-2 disabled:opacity-50" disabled={shareBusy} onClick={downloadShareImage}
                style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", border: "none", fontWeight: 700 }}>
                <Share size={18} weight="fill" /> {shareBusy ? "Creating…" : "Share my achievement"}
              </button>
            )}
            {shareAllowed && !isToken && (
              <button data-testid="cert-linkedin-button" className="btn-secondary flex items-center gap-2" onClick={shareLinkedIn} style={{ background: "#0A66C2", color: "white", border: "none" }}>
                <LinkedinLogo size={18} weight="fill" /> Share on LinkedIn
              </button>
            )}
            {shareAllowed && (
              <button data-testid="cert-share-button" className="btn-ghost flex items-center gap-1.5 text-[#CCFF00]" onClick={shareGeneric}>
                <Share size={16} weight="duotone" /> Share link
              </button>
            )}
          </div>

          {/* QR code for offline verification */}
          <div className="mt-6 flex items-start gap-4 flex-wrap" data-testid="cert-qr-section">
            <div className="p-2 rounded-xl bg-white inline-block">
              <QRCodeSVG value={verifyUrl} size={104} bgColor="#FFFFFF" fgColor="#000000" includeMargin={false} />
            </div>
            <div className="flex-1 min-w-[180px]">
              <div className="text-xs uppercase tracking-[0.2em] text-[#000000] mb-1">Scan to verify</div>
              <div className="text-sm text-[#000000]">
                Anyone can scan this QR code (or visit the link) to verify the certificate's authenticity — no login required.
              </div>
              <div className="text-[10px] text-[#000000] font-mono mt-2 truncate">{verifyUrl}</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
